
import { NeedViewModel } from '../ViewModels';
import { NeedHeader } from '../need-config';
import { useNeedEnricher } from './useNeedEnricher';
import {
    ActionStatusHelper,
    DomainTableConfig,
    useAppEnum,
    ActionResponse,
    useLayoutLifeCycle,
} from '@/Shared';
import { DOMAIN_CONFIGS, DomainHookProps } from '@/Shared/Config/domain-config';
import { useNeed } from '@/OneLiners';
import { NeedModel } from '../NeedModel';


/**
 * useNeedTableConfig hook
 *
 * Domain-specific layout composition hook for Need entities.
 * Orchestrates domain data, enrichment, permissions, and UI table configuration.
 *
 * Responsibilities:
 * - Aggregate Need domain state, enrichment, and permissions into a single response.
 * - Provide dynamic headers for Need table (sector & city dependent).
 * - Map strict Model → ViewModel for UI consumption.
 * - Delegate lifecycle orchestration to useLayoutLifeCycle.
 *
 * @date: March 2, 2026
 * @author: Samano CASTRE
 * 
 * @see DomainHookProps
 * @see DomainTableConfig<NeedViewModel>
 *
 * @param props Domain-level configuration and permissions
 * @returns DomainTableConfig<NeedViewModel>
 */
export function useNeedTableConfig(props: DomainHookProps): DomainTableConfig<NeedViewModel> {
    const { permissionHook, accessRight } = props;
    const { domainType } = DOMAIN_CONFIGS.Need;

    const { NeedEnums } = useAppEnum();

    /**
     * Need domain CRUD and state
     */
    const needHook = useNeed();

    /**
     * Enrichment layer
     * Resolves derived fields and cross-domain data
     */
    const {
        sectorMap,
        cityMap,
        getStrictEnrichedNeed,
        enricherResponse,
    } = useNeedEnricher();

    /**
     * Combined readiness response
     * Only builds table once all sources succeeded
     */
    const response: ActionResponse = {
        status: ActionStatusHelper.combine(
            needHook.stateResponse.status,
            enricherResponse.status,
            permissionHook.stateResponse.status
        ),
        error: needHook.stateResponse.error ??
            enricherResponse.error
    };

    /**
        * Delegation to generic layout lifecycle.
        */
    const lifeCycleHook = useLayoutLifeCycle({
        response,
        domainType,
        props,
        entityHook: needHook,

        /**
         * Header configuration for Property table.
         */
        headers: NeedHeader.build(sectorMap, cityMap, NeedEnums),

        /**
         * Strict Model → ViewModel transformation.
         */
        toViewModelFn: (need: NeedModel, isDisplaying: boolean) =>
            new NeedViewModel({
                need: getStrictEnrichedNeed(need),
                viewEnums: NeedEnums,
                accessRight,
                isDisplaying
            })
    });

    return lifeCycleHook;
}